import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './OpsKb.css';
import {
  Row, Col, Card, CardBody, Input, Button, Badge, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, Label,
} from 'reactstrap';
import {
  useKbList, useKbArticle, useCreateKbArticle, useUpdateKbArticle, useDeleteKbArticle,
} from './useOpsKb';

/**
 * Interne Ops-Knowledge-Base / Runbook-Hub. Getrennt von der kunden-seitigen
 * Fehler-KB (errorDB/LogDB). Speist u.a. die eingespeisten Repo-Runbooks
 * (source='repo') + operator-verfasste Artikel.
 */
export function OpsKb() {
  // Deep-Link vom Ticket: ?article=<slug> öffnet direkt, ?q=<text> prefüllt die Suche.
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [source, setSource] = useState('');
  const [selected, setSelected] = useState(params.get('article') || null);
  const [editing, setEditing] = useState(null); // null | {} (neu) | article (bearbeiten)

  const list = useKbList({ q, source });
  const detail = useKbArticle(selected);
  const items = list.data?.items ?? [];

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Ops-Wissensbasis / Runbooks</h4>
        <Button color="primary" size="sm" onClick={() => setEditing({})}>+ Artikel</Button>
      </div>

      <Row>
        <Col md={5} lg={4}>
          <Card className="mb-3">
            <CardBody>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Volltextsuche (Titel + Inhalt)…"
                className="mb-2"
              />
              <div className="btn-group btn-group-sm d-flex mb-3" role="group">
                {[['', 'Alle'], ['repo', 'Repo-Runbooks'], ['manual', 'Eigene']].map(([v, label]) => (
                  <Button key={v} outline color="secondary" active={source === v} onClick={() => setSource(v)}>
                    {label}
                  </Button>
                ))}
              </div>

              {list.isLoading ? (
                <Spinner size="sm" />
              ) : items.length === 0 ? (
                <p className="small text-muted mb-0">Keine Artikel. Repo-Runbooks per <code>bin/import-runbooks.mjs</code> einspeisen.</p>
              ) : (
                <div className="list-group">
                  {items.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setSelected(a.slug)}
                      className={`list-group-item list-group-item-action ${selected === a.slug ? 'active' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <span className="fw-medium">{a.title}</span>
                        {a.source === 'repo' && <Badge color="info" pill>repo</Badge>}
                      </div>
                      <div className="small text-muted">{a.category}{a.tags?.length ? ` · ${a.tags.join(', ')}` : ''}</div>
                    </button>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={7} lg={8}>
          <Card>
            <CardBody>
              {!selected ? (
                <p className="text-muted mb-0">Wähle links einen Artikel.</p>
              ) : detail.isLoading ? (
                <Spinner size="sm" />
              ) : detail.data?.article ? (
                <ArticleView article={detail.data.article} onEdit={setEditing} onDeleted={() => setSelected(null)} />
              ) : (
                <p className="text-danger mb-0">Artikel konnte nicht geladen werden.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {editing && (
        <ArticleEditor
          article={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(slug) => { setEditing(null); if (slug) setSelected(slug); }}
        />
      )}
    </div>
  );
}

function ArticleView({ article, onEdit, onDeleted }) {
  const del = useDeleteKbArticle();
  return (
    <>
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h5 className="mb-1">{article.title}</h5>
          <div className="small text-muted">
            {article.category}
            {article.source === 'repo' && article.sourcePath && <> · <code>{article.sourcePath}</code></>}
            {article.tags?.length ? ` · ${article.tags.join(', ')}` : ''}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" outline color="secondary" onClick={() => onEdit(article)}>Bearbeiten</Button>
          <Button
            size="sm" outline color="danger"
            disabled={del.isPending}
            onClick={() => { if (window.confirm('Artikel löschen?')) del.mutate({ id: article.id }, { onSuccess: onDeleted }); }}
          >Löschen</Button>
        </div>
      </div>
      {/* Gerendertes Markdown (GFM: Tabellen/Task-Lists). react-markdown rendert
          KEIN rohes HTML → kein XSS-Risiko aus Artikel-Inhalten. */}
      <div className="kb-markdown border rounded p-3" style={{ maxHeight: '65vh', overflow: 'auto' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.bodyMd || ''}</ReactMarkdown>
      </div>
    </>
  );
}

function ArticleEditor({ article, onClose, onSaved }) {
  const isNew = !article;
  const create = useCreateKbArticle();
  const update = useUpdateKbArticle();
  const [title, setTitle] = useState(article?.title ?? '');
  const [category, setCategory] = useState(article?.category ?? 'general');
  const [tags, setTags] = useState((article?.tags ?? []).join(', '));
  const [bodyMd, setBodyMd] = useState(article?.bodyMd ?? '');
  const pending = create.isPending || update.isPending;

  function save() {
    const payload = {
      title: title.trim(),
      category: category.trim() || 'general',
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      bodyMd,
    };
    if (isNew) create.mutate(payload, { onSuccess: (r) => onSaved(r?.article?.slug) });
    else update.mutate({ id: article.id, ...payload }, { onSuccess: () => onSaved(article.slug) });
  }

  const valid = title.trim() && bodyMd.trim();
  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>{isNew ? 'Neuer Artikel' : 'Artikel bearbeiten'}</ModalHeader>
      <ModalBody>
        <Label className="small">Titel</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mb-2" maxLength={200} />
        <Row>
          <Col sm={6}>
            <Label className="small">Kategorie</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mb-2" />
          </Col>
          <Col sm={6}>
            <Label className="small">Tags (kommagetrennt)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} className="mb-2" />
          </Col>
        </Row>
        <Label className="small">Inhalt (Markdown)</Label>
        <Input type="textarea" rows={14} value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} style={{ fontFamily: 'monospace' }} />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
        <Button color="primary" disabled={!valid || pending} onClick={save}>{pending ? 'Speichere…' : 'Speichern'}</Button>
      </ModalFooter>
    </Modal>
  );
}
